import {
  Stack,
  Card,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { BaseTable } from "../BaseTable/BaseTable";
import { useSelector, useDispatch } from "react-redux";
import {
  paymentGatewayListColumns,
  paymentGatewayImportColumns,
  paymentGatewaySampleRow,
} from "./PaymentGateway.constants";
import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { alertSlice } from "src/store/alert/alert.slice";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { Icon } from '@iconify/react';
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { fetchWithError, fetchApi } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { paymentGatewayDetailsBulkUrl } from "src/react-query/endPoints";
import { downloadCSV } from "src/utils/convertAnddownloadCsv";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

export const PaymentGatewayList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const isGrandParent = useIsGrandParent();
  const paymentGatewayViewAccess = useAccess(FEATURE.PAYMENTGATEWAY, "V");
  const paymentGatewayCreateAccess = useAccess(FEATURE.PAYMENTGATEWAY, "C");

  // Check if Grand Parent Company is selected (teamId matches user.company.id)
  const isGrandParentCompanySelected = useMemo(() => {
    return String(teamId) === String(user?.company?.id);
  }, [teamId, user?.company?.id]);

  // Mirrors requireSuperAdminFeature(PAYMENTGATEWAY) on the API: the ROOT super
  // admin is unlimited, everyone else is judged on the PaymentGateway feature.
  // Finance access deliberately does NOT grant this — the API never consulted
  // it, so using it here surfaced buttons the server then rejected with 403.
  // Step 1 of the API gate is the tenant check: payment gateways belong to the
  // Grand Parent company, and a non-GP login is rejected whatever its role says.
  // Role edits replicate cms_access down the hierarchy, so a company-level user
  // can hold a PaymentGateway grant the server will never honour.
  const isAuthorized = isGrandParent && (isRootSuperAdmin || paymentGatewayViewAccess);
  const hasCreateAccess =
    isGrandParent &&
    (isRootSuperAdmin || paymentGatewayCreateAccess) &&
    isGrandParentCompanySelected;
  const isReadOnly = isAuthorized && !hasCreateAccess;
  const { data: paymentGateways, isFetching, refetch } = useQuery({
    queryKey: queryKeys.paymentGatewayDetails(),
    queryFn: fetchWithError,
    select: selector,
  });
  const { open, data } = useSelector(({ dialog }) => dialog);
  const handleClose = () => dispatch(dialogSlice.actions.setDialog(false));
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isAuthorized) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized - Grand Parent Company Access Required" })
      );
      navigate("/dashboard");
    }
  }, [isAuthorized, dispatch, navigate]);

  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/payment-gateways/${id}`);
  };

  useEffect(() => {
    if (!isFetching && paymentGateways) {
      // Ensure paymentGateways is an array
      const gatewaysArray = Array.isArray(paymentGateways) ? paymentGateways : [];
      // Ensure each row has an id property
      const dataWithIds = gatewaysArray.map((gateway) => ({
        id: gateway.id,
        ...gateway
      }));
      setTableData(dataWithIds);
    } else if (!isFetching && !paymentGateways) {
      setTableData([]);
    }
  }, [isFetching, paymentGateways]);

  const handleSearching = (event) => {
    const dataToSearchFrom = Array.isArray(paymentGateways) ? paymentGateways : [];
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);
    
    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "name",
            "gateway_type",
            "currency",
            "country",
            "domain",
            "key_id",
            "charger_id",
            "connector_id"
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) => 
              value ? value.toString().toLowerCase() : ""
            );
          
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm)
          );
          
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, {
              id: currentObj.id,
              ...currentObj
            }];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData(paymentGateways?.map(gateway => ({
        id: gateway.id,
        ...gateway
      })) ?? []);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  // Build and download a sample CSV with the accepted columns and one example row.
  const handleDownloadSample = () => {
    const header = paymentGatewayImportColumns.join(",");
    const exampleRow = paymentGatewayImportColumns
      .map((col) => {
        const value = paymentGatewaySampleRow[col] ?? "";
        // Quote values containing a comma so the CSV stays valid.
        return value.toString().includes(",") ? `"${value}"` : value;
      })
      .join(",");
    downloadCSV(`${header}\n${exampleRow}`, "payment_gateway_sample");
  };

  // Parse the selected CSV file into an array of row objects keyed by header.
  const parseCsvFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          // Keep only known columns and trim whitespace from values.
          const rows = json.map((row) => {
            const cleaned = {};
            paymentGatewayImportColumns.forEach((col) => {
              const value = row[col];
              cleaned[col] =
                value === undefined || value === null
                  ? ""
                  : value.toString().trim();
            });
            return cleaned;
          });
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });

  const handleImportClick = () => {
    setImportResult(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    try {
      const rows = await parseCsvFile(file);
      if (!rows.length) {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: "The CSV file is empty or has no data rows.",
            severity: "error",
          })
        );
        return;
      }

      const response = await fetchApi({
        url: paymentGatewayDetailsBulkUrl,
        method: "POST",
        body: { rows },
      });

      setImportResult(response);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: response?.message || "Import completed.",
          severity: response?.summary?.failed ? "warning" : "success",
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to import CSV.",
          severity: "error",
        })
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <Page
      {...(hasCreateAccess && { path: `/payment-gateways/new` })}
      actionButtonText="Add New Payment Gateway"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Manage Payment Gateways
          </Typography>
          {searchValue && (
            <Chip
              label={`${tableData?.length || 0} results`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search payment gateways"
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDownloadSample}
          >
            Sample CSV
          </Button>
          {hasCreateAccess && (
            <Button
              variant="contained"
              size="small"
              onClick={handleImportClick}
              disabled={importing}
            >
              {importing ? "Importing..." : "Import CSV"}
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileSelected}
          />
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          isLoading={isFetching}
          columns={paymentGatewayListColumns(isReadOnly)}
          showExportCsvBtn={false}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          getRowId={(row) => row.id}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            }
          }}
        />
      </StyledListCard>

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Result</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Total: {importResult?.summary?.total ?? 0} &nbsp;|&nbsp; Inserted:{" "}
            {importResult?.summary?.inserted ?? 0} &nbsp;|&nbsp; Failed:{" "}
            {importResult?.summary?.failed ?? 0}
          </Typography>
          {importResult?.errors?.length ? (
            <>
              <Typography
                variant="subtitle2"
                color="error"
                sx={{ mt: 2, mb: 1 }}
              >
                The following rows were not imported:
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>key_id</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResult.errors.map((err) => (
                    <TableRow key={`${err.row}-${err.key_id ?? "na"}`}>
                      <TableCell>{err.row}</TableCell>
                      <TableCell>{err.key_id ?? "-"}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              All rows imported successfully.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

